import { useState } from "react";
import { Link } from "react-router";
import { ErrorPanel } from "@/components/ErrorPanel";
import { Spinner } from "@/components/Spinner";
import { useToast } from "@/components/Toast";
import { usePageTitle } from "../pages/usePageTitle";
import { type BlogPost, useAllPosts, useDeletePost } from "./api";
import { BlogComposer } from "./BlogComposer";
import { formatDate } from "./format";
import "./admin.css";

/**
 * /admin/blog: list every post (drafts included), create, edit, delete. Admins and `blogAdmin`
 * authors only (RequireAdmin allowBlogAdmin in routes.tsx; the backend checks the claims too).
 * Ported from the legacy AdminBlog with the app's tokens, so it follows the app's theme.
 */
export function AdminBlogPage() {
  usePageTitle("Manage posts");
  const toast = useToast();
  const query = useAllPosts();
  const del = useDeletePost();
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [open, setOpen] = useState(false);

  const openNew = () => {
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (post: BlogPost) => {
    setEditing(post);
    setOpen(true);
  };
  const remove = (post: BlogPost) => {
    if (!window.confirm(`Delete “${post.title}”? This cannot be undone.`)) return;
    del.mutate(post.slug, {
      onSuccess: () => toast("Post deleted."),
      onError: (e) => toast(e instanceof Error ? e.message : "Could not delete the post."),
    });
  };

  return (
    <div className="sadmin">
      <div className="sadmin-in">
        <div className="sadmin-head">
          <div>
            <div className="sadmin-eyebrow">Admin · Blog</div>
            <h1>Manage posts</h1>
          </div>
          <div className="sadmin-actions">
            <Link className="wp-btn g" to="/blog">
              View blog
            </Link>
            <button type="button" className="wp-btn" onClick={openNew}>
              + New post
            </button>
          </div>
        </div>

        <div className="sadmin-card">
          {query.isPending ? (
            <Spinner label="Loading posts" />
          ) : query.isError ? (
            <ErrorPanel error={query.error} onRetry={() => query.refetch()} />
          ) : query.data.length === 0 ? (
            <div className="sadmin-empty">
              <b>No posts yet</b>
              <p>Create your first post with &ldquo;New post&rdquo;.</p>
            </div>
          ) : (
            <table className="sadmin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th className="r">Actions</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((post) => (
                  <tr key={post.slug}>
                    <td>
                      <button type="button" className="sadmin-title" onClick={() => openEdit(post)}>
                        {post.title}
                      </button>
                      <div className="sadmin-slug">/{post.slug}</div>
                    </td>
                    <td>{post.category}</td>
                    <td>
                      <span className={`sadmin-pill ${post.published ? "pub" : "draft"}`}>
                        {post.published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="nowrap">
                      {formatDate(post.published ? post.publishedAt : post.updatedAt)}
                    </td>
                    <td className="r">
                      <button type="button" className="wp-btn g" onClick={() => openEdit(post)}>
                        Edit
                      </button>{" "}
                      <button
                        type="button"
                        className="wp-btn g danger"
                        disabled={del.isPending}
                        onClick={() => remove(post)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {open && <BlogComposer initialPost={editing} onClose={() => setOpen(false)} />}
    </div>
  );
}
